import { DialogComponent } from '@theme/dialog';
import { onAnimationEnd, preloadImage } from '@theme/utilities';

/**
 * A custom element that manages a wishlist drawer with fly-to-wishlist animation.
 *
 * @extends {DialogComponent}
 */
class WishlistDrawerComponent extends DialogComponent {
  constructor() {
    super();
    this.localStorageKey = 'sr_wishlist_horizon_v1';
    this.headerObserver = null;
    this.cardObserver = null;
    this.moneyFormat = '';
    this.showBtnText = false;
    this.showDividers = false;
    this.removeBtnText = '';
    this.addToWishlist = '';
    this.inWishlist = '';
    this.buttonStyle = '';
    this.buttonColorScheme = '';
    this.shouldOpen = false;
    this.showFlyAnimation = true; // New property to control animation
  }

  connectedCallback() {
    super.connectedCallback();
    this.initializeSettings();
    this.setupWishlistSystem();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanup();
  }

  initializeSettings() {
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.showBtnText = this.dataset.displayButtonText === 'true';
    this.showDividers = this.dataset.displayDividers === 'true';
    this.removeBtnText = this.dataset.textButtonRemove || 'Remove';
    this.addToWishlist = this.dataset.textAddToWishlist || 'Add to Wishlist';
    this.inWishlist = this.dataset.textInWishlist || 'In Wishlist';
    this.buttonStyle = this.dataset.buttonStyle || 'button-secondary';
    this.buttonColorScheme = this.dataset.buttonColorScheme || 'color-scheme-1';
    this.shouldOpen = this.dataset.openOnAdd === 'true';
    this.showFlyAnimation = this.dataset.showFlyAnimation !== 'false'; // Default to true
  }

  setupWishlistSystem() {
    this.injectWishlistHeader();
    this.insertProductWishlistBtn();
    this.insertCardWishlistBtn();
    this.initWishlist();
    this.buildWishlistDrawer();
    this.enableWishlistBackdropClose();
    this.watchForHeaderChanges();
    
    // Make the click handler globally available
    window.onClickSrWishlistButton = this.onClickSrWishlistButton.bind(this);
  }

  cleanup() {
    if (this.headerObserver) {
      this.headerObserver.disconnect();
      this.headerObserver = null;
    }
    if (this.cardObserver) {
      this.cardObserver.disconnect();
      this.cardObserver = null;
    }
    if (window.onClickSrWishlistButton) {
      delete window.onClickSrWishlistButton;
    }
  }

  formatMoneyHelper(cents, format = this.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }

    const formatType = format.match(placeholderRegex)[1];
    const value = {
      amount: formatWithDelimiters(cents, 2),
      amount_no_decimals: formatWithDelimiters(cents, 0),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ',')
    }[formatType];

    return format.replace(placeholderRegex, value);
  }

  buildWishlistButtonHTML(className = '', showText = false) {
    const addClass = showText ? 'add-to-wishlist-text' : 'visually-hidden';
    const removeClass = showText ? 'add-to-wishlist-text--added' : 'visually-hidden';

    return `
    <button 
      class="button-wishlist ${className.trim()}" 
      type="button"
      onclick="onClickSrWishlistButton(this, event)"
      aria-controls="sr-wishlist-drawer"
      style="display: none;">
      <span data-add>
        <span class="svg-wrapper wishlist-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
          </svg>
        </span>
        <span class="${addClass}">${this.addToWishlist}</span>
      </span>
      <span data-remove>
        <span class="svg-wrapper wishlist-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
          </svg>
        </span>
        <span class="${removeClass}">${this.inWishlist}</span>
      </span>
    </button>`;
  }

  injectWishlistHeader() {
    if (document.querySelector('#wishlist-icon')) return;

    const headerActions = document.querySelector('.header header-actions');
    if (!headerActions) return;
  
    const link = document.createElement('a');
    link.id = 'wishlist-icon';
    link.className = 'wishlist-button header-actions__action';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-controls', 'sr-wishlist-drawer');
  
    link.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" d="M13.85,3c-2.58,0-3.85,2.55-3.85,2.55,0,0-1.27-2.55-3.85-2.55-2.1,0-3.76,1.76-3.78,3.85-.04,4.35,3.45,7.44,7.28,10.04.22.15.5.15.72,0,3.83-2.6,7.32-5.69,7.28-10.04-.02-2.09-1.68-3.85-3.78-3.85h0Z"/>
      </svg>
      <div class="wishlist-bubble" data-maintain-ratio>
        <span class="wishlist-bubble__background">
          <span class="visually-hidden">Total items in wishlist: <span class="wishlist-count">0</span></span>
          <span class="wishlist-bubble__text-count" aria-hidden="true"><span class="wishlist-count">0</span></span>
        </span>
      </div>
    `;
  
    link.addEventListener('click', () => {
      this.open();
    });
  
    headerActions.appendChild(link);
  }

  watchForHeaderChanges() {
    const headerActions = document.querySelector('.header header-actions');
    if (!headerActions) return;
  
    this.headerObserver = new MutationObserver(() => {
      if (!document.querySelector('#wishlist-icon')) {
        this.injectWishlistHeader();
        this.initWishlist();
      }
    });
  
    this.headerObserver.observe(headerActions, {
      childList: true,
      subtree: true
    });
  }

  insertProductWishlistBtn() {
    if (this.dataset.displayProductButton === 'false') return;

    // Assign data-product-handle from current page URL
    const productTemplate = document.querySelector('[data-template="product"] .product-information');
    if (productTemplate) {
      const currentUrl = window.location.pathname;
      const match = currentUrl.match(/\/products\/([^\/?#]+)/);
      const productHandle = match?.[1];
      
      if (productHandle && !productTemplate.dataset.productHandle) {
        productTemplate.setAttribute('data-product-handle', productHandle);
      }
    }
    
    const productForm = document.querySelector('.product-information add-to-cart-component');
    if (!productForm) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'button-wishlist-wrapper';

    const wishlistButton = document.createElement('div');
    wishlistButton.innerHTML = this.buildWishlistButtonHTML(`button wishlist-button--custom ${this.buttonStyle}`, this.showBtnText);

    const parent = productForm.parentElement;
    parent.insertBefore(wrapper, productForm.nextSibling);
    wrapper.appendChild(productForm);
    wrapper.appendChild(wishlistButton.firstElementChild);
  }

  insertCardWishlistBtn() {
    if (this.dataset.displayCardButton === 'false') return;
  
    // Assign data-product-handle attr
    const setProductHandle = (card) => {
      const productUrl = card.querySelector('a')?.getAttribute('href');
      const match = productUrl?.match(/\/products\/([^\/?#]+)/);
      const handle = match?.[1];
    
      if (handle && !card.dataset.productHandle) {
        card.dataset.productHandle = handle;
      }
    };
  
    const injectButtons = (container = document) => {
      container.querySelectorAll('.product-card').forEach((card) => {
        setProductHandle(card);

        const cardInner = card.querySelector('.card-gallery');
        if (!cardInner) return;
  
        if (!cardInner.querySelector('.button-wishlist')) {
          cardInner.insertAdjacentHTML(
            'beforeend',
            this.buildWishlistButtonHTML(`product-card__button-wishlist ${this.buttonColorScheme}`, false)
          );
        }
      });
    };
  
    // Inject initially
    injectButtons();
    this.initWishlist();
  
    // Watch for dynamically added product cards
    this.cardObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
  
          // If node is a product-card
          if (node.matches?.('.product-card')) {
            injectButtons(node.parentElement || node);
            this.initWishlist();
          }
  
          // If node contains product cards
          const nested = node.querySelectorAll?.('.product-card');
          if (nested?.length) {
            injectButtons(node);
            this.initWishlist();
          }
        });
      });
    });
  
    this.cardObserver.observe(document.body, { childList: true, subtree: true });
  }

  initWishlist() {
    const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];

    document.querySelectorAll('.button-wishlist').forEach((btn) => {
      const productHandle = btn.closest('[data-product-handle]')?.dataset.productHandle;
      const isWishlisted = wishlist.some((item) => item.handle === productHandle);

      btn.querySelector('[data-add]').style.display = isWishlisted ? 'none' : 'flex';
      btn.querySelector('[data-remove]').style.display = isWishlisted ? 'flex' : 'none';
      btn.classList.toggle('in-wishlist', isWishlisted);
      btn.style.display = 'flex';
    });

    document.querySelectorAll('.wishlist-count').forEach((el) => el.textContent = `${wishlist.length}`);
    document.querySelectorAll('.wishlist-bubble').forEach((bubble) => {
      bubble.classList.toggle('is-visible', wishlist.length > 0);
    });
  }

  buildWishlistDrawer() {
    const drawer = this;
    const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
    const drawerOpen = this.refs?.dialog?.hasAttribute('open');
    const drawerEmpty = drawer.querySelector('.wishlist-drawer__empty');
    const drawerContent = drawer.querySelector('.wishlist-drawer__content');
    const drawerProductList = drawer.querySelector('.wishlist-drawer__items');

    drawerProductList.innerHTML = '';

    if (!wishlist.length) {
      drawer.classList.add("wishlist-empty");
      drawerEmpty.style.display = 'flex';
      drawerContent.style.display = 'none';
      drawerProductList.innerHTML = '';
      return;
    }

    drawer.classList.remove("wishlist-empty");
    drawerEmpty.style.display = 'none';
    drawerContent.style.display = 'flex';

    drawerProductList.innerHTML = wishlist.map((product) => `
      <li class="wishlist-item ${this.showDividers ? 'wishlist-item--dividers' : ''}">
        <div class="wishlist-item__row">
          <a href="${product.url}">
            <img
              class="wishlist-item__media"
              src="${product.featured_image}&width=250"
              srcset="${product.featured_image}&width=250 250w"
              sizes="250px"
              width="250"
              height="auto"
              alt="${product.title}"
              loading="lazy"
            />
          </a>
          <div class="wishlist-item__details">
            <a href="${product.url}" class="wishlist-item__product-title">${product.title}</a>
            <div class="wishlist-item__price">
              <span class="visually-hidden">Sale price</span>
              <span>${this.formatMoneyHelper(product.price)}</span>
              <span class="compare-at-price" style="${product.compare_at_price > product.price ? '' : 'display: none;'}">
                <span class="visually-hidden">Regular price</span>
                ${this.formatMoneyHelper(product.compare_at_price)}
              </span>
            </div>
          </div>
          <button class="wishlist-item__remove button-unstyled" type="button" aria-label="${this.removeBtnText}" data-product-handle="${product.handle}">
            <span class="visually-hidden">${this.removeBtnText}</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path class="remove-icon-bottom" d="M11.5 9v4.25M8.5 9v4.25M5.75 12.2V6h8.5c0 2.421 0 3.779 0 6.2 0 .853 0 1.447-.038 1.91-.037.453-.106.714-.207.911a2.498 2.498 0 0 1-.983 1.017c-.197.1-.458.17-.911.207-.463.037-1.057.038-1.91.038h-.4c-.853 0-1.447 0-1.91-.038-.453-.037-.714-.106-.911-.207a2.498 2.498 0 0 1-.984-1.017c-.1-.197-.17-.458-.207-.911C5.75 13.647 5.75 13.053 5.75 12.2z" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
              <path class="remove-icon-top" d="M4.25 6h11.5M8 5.25a2 2 0 1 1 4 0" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
            </svg>
          </button>
        </div>
      </li>`).join('');

    drawerProductList.querySelectorAll('.wishlist-item__remove').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
        const updated = wishlist.filter((item) => item.handle !== btn.dataset.productHandle);
        localStorage.setItem(this.localStorageKey, JSON.stringify(updated));
        this.initWishlist();
        this.buildWishlistDrawer();
      });
    });
  }

  enableWishlistBackdropClose() {
    const wishlistDialog = this.querySelector('dialog');
    if (!wishlistDialog) return;
  
    wishlistDialog.addEventListener('click', (e) => {
      const rect = wishlistDialog.getBoundingClientRect();
      const clickedOutside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;
  
      if (clickedOutside) {
        this.close();
      }
    });
  }

  /**
   * Animates the fly to wishlist animation.
   * @param {HTMLElement} sourceButton - The button that was clicked
   * @param {string} productImage - The product image URL
   */
  #animateFlyToWishlist(sourceButton, productImage) {
    if (!this.showFlyAnimation) return;
    
    const wishlistIcon = document.querySelector('#wishlist-icon');
    
    if (!wishlistIcon || !sourceButton || !productImage) return;

    const flyToWishlistElement = document.createElement('fly-to-wishlist');
    
    flyToWishlistElement.style.setProperty('background-image', `url(${productImage})`);
    flyToWishlistElement.source = sourceButton;
    flyToWishlistElement.destination = wishlistIcon;
    
    document.body.appendChild(flyToWishlistElement);
  }

  async onClickSrWishlistButton(btn, event) {
    event.preventDefault();
    event.stopPropagation();

    const productHandle = btn.closest('[data-product-handle]')?.dataset.productHandle;
    let wishlist = JSON.parse(localStorage.getItem(this.localStorageKey)) || [];
    const isWishlisted = wishlist.some((item) => item.handle === productHandle);

    if (isWishlisted) {
      wishlist = wishlist.filter((item) => item.handle !== productHandle);
    } else {
      try {
        const res = await fetch(`/products/${productHandle}.js`);
        const product = await res.json();
        
        // Preload the product image for the animation
        if (product.featured_image) {
          preloadImage(product.featured_image);
        }
        
        wishlist.push({
          id: product.id,
          handle: productHandle,
          url: product.url,
          title: product.title,
          compare_at_price: product.compare_at_price,
          compare_at_price_varies: product.compare_at_price_varies,
          price: product.price,
          featured_image: product.featured_image,
          vendor: product.vendor,
          time: Date.now()
        });

        // Trigger fly-to-wishlist animation for newly added items
        this.#animateFlyToWishlist(btn, product.featured_image + '&width=100');
        
      } catch (err) {
        console.error('Failed to fetch product data:', err);
      }
    }
  
    localStorage.setItem(this.localStorageKey, JSON.stringify(wishlist));
    this.initWishlist();
    this.buildWishlistDrawer();
  
    if (!isWishlisted && this.shouldOpen && !this.querySelector('dialog')?.open) {
      // Use animation duration if animation is enabled, otherwise use shorter delay
      const delay = this.showFlyAnimation ? 600 : 60;
      
      setTimeout(() => {
        requestAnimationFrame(() => {
          this.open();
        });
      }, delay);
    }
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}

/**
 * A custom element that animates a product image flying to the wishlist icon.
 */
class FlyToWishlist extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    // Define bezier curve points
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    // Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    // Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying element along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = this.#bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      // Update the position of the flying element
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the wishlist
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      // Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Fade out the flying element
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying element at the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    // Start the animation
    requestAnimationFrame(animate);
  }

  /**
   * Calculates a point on a cubic Bézier curve.
   * @param {number} t - The parameter value (0 <= t <= 1).
   * @param {{x: number, y: number}} p0 - The starting point (x, y).
   * @param {{x: number, y: number}} p1 - The first control point (x, y).
   * @param {{x: number, y: number}} p2 - The second control point (x, y).
   * @param {{x: number, y: number}} p3 - The ending point (x, y).
   * @returns {{x: number, y: number}} The point on the curve.
   */
  #bezierPoint(t, p0, p1, p2, p3) {
    const cX = 3 * (p1.x - p0.x);
    const bX = 3 * (p2.x - p1.x) - cX;
    const aX = p3.x - p0.x - cX - bX;

    const cY = 3 * (p1.y - p0.y);
    const bY = 3 * (p2.y - p1.y) - cY;
    const aY = p3.y - p0.y - cY - bY;

    const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
    const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

    return { x, y };
  }
}

if (!customElements.get('fly-to-wishlist')) {
  customElements.define('fly-to-wishlist', FlyToWishlist);
}